import React, { useEffect, useState } from "react";
import { WifiOff, RefreshCcw } from "lucide-react";

/** Simple network-status banner + SW-update prompt.
 *  Never manipulates auth or data — purely UI signalling. */
export const NetworkStatus = () => {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [updateReg, setUpdateReg] = useState(null);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    const onSwUpdate = (e) => setUpdateReg(e.detail);
    window.addEventListener("dd-sw-update", onSwUpdate);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      window.removeEventListener("dd-sw-update", onSwUpdate);
    };
  }, []);

  const applyUpdate = () => {
    if (updateReg?.waiting) updateReg.waiting.postMessage({ type: "SKIP_WAITING" });
    else window.location.reload();
  };

  return (
    <>
      {!online && (
        <div data-testid="offline-banner"
             className="sticky top-0 z-40 bg-red-600 text-white text-xs px-3 py-2 font-display uppercase tracking-widest flex items-center justify-center gap-2">
          <WifiOff size={14} />
          You are offline — job updates, uploads and status changes are disabled until you reconnect.
        </div>
      )}
      {updateReg && (
        <div data-testid="sw-update-banner"
             className="sticky top-0 z-40 bg-[#f28a16] text-[#111619] text-xs px-3 py-2 font-display uppercase tracking-widest flex items-center justify-center gap-3">
          A new version of the app is ready.
          <button onClick={applyUpdate} data-testid="sw-update-apply"
                  className="inline-flex items-center gap-1 bg-[#111619] text-white rounded-md px-3 py-1">
            <RefreshCcw size={12} /> Reload
          </button>
        </div>
      )}
    </>
  );
};

/** Deferred BeforeInstallPromptEvent handler for A2HS. */
export const InstallPrompt = () => {
  const [evt, setEvt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("dd_install_dismissed_v1") === "1"; } catch { return false; }
  });

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!evt || dismissed) return null;

  const install = async () => {
    try { evt.prompt(); await evt.userChoice; } finally { setEvt(null); }
  };
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("dd_install_dismissed_v1", "1"); } catch {}
  };

  return (
    <div data-testid="install-prompt"
         className="fixed bottom-24 inset-x-3 z-40 bg-[#111619] text-white rounded-md p-3 shadow-2xl border border-[#f28a16]/40 flex items-center gap-3">
      <div className="flex-1 text-xs">
        Install <strong>D&amp;D Team</strong> on your home screen — full-screen, faster launch.
      </div>
      <button onClick={install} data-testid="install-accept"
              className="bg-[#f28a16] text-[#111619] rounded-md px-3 py-1.5 text-xs font-display uppercase tracking-widest">Install</button>
      <button onClick={dismiss} data-testid="install-dismiss"
              className="text-xs text-white/60 hover:text-white">Not now</button>
    </div>
  );
};
